import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Activity, Calendar, FileText, PencilIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DocumentDetailLayout,
  DocumentReviewDetailItem,
} from "@/components/request-detail";
import { useGetProjectOKRByIdQuery } from "@/services/(company)/api.projects";
import type { ISingleOKR } from "@/types/(company)/projects.interface";
import {
  projectIdKey,
  okrIdKey,
} from "@/routes/company/routes.projects.constants";
import { PATHS } from "@/routes/paths";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLocale } from "@/hooks/useLocale";
import { withBranding } from "@/utils/helper";

export const ProjectOKRDetailPage = () => {
  const { t } = useTranslation(["projects", "common"]);
  const { formatDate } = useLocale();
  usePageTitle(withBranding(t("projects:okr_detail")));
  const navigate = useNavigate();
  const params = useParams();
  const projectId = params[projectIdKey];
  const okrId = params[okrIdKey];

  const { state } = useLocation() as { state: { okr: ISingleOKR } | null };

  const { data, isLoading } = useGetProjectOKRByIdQuery(
    { projectId: projectId ?? "", okrId: okrId ?? "" },
    { skip: !projectId || !okrId },
  );

  // API data is authoritative; state is instant fallback while loading
  const okr = (data?.data ?? state?.okr) as ISingleOKR | undefined;

  if (!projectId || !okrId) return null;

  return (
    <DocumentDetailLayout
      title={t("projects:okr_detail")}
      status={okr?.status}
      onBack={() => navigate(-1)}
      isLoading={isLoading && !state?.okr}
      footer={
        okr ? (
          <>
            <Button variant="outline" onClick={() => navigate(-1)}>
              {t("common:buttons.back")}
            </Button>
            <Button
              variant="primary"
              onClick={() =>
                navigate(
                  PATHS.COMPANY.COMPANY_PROJECTS_PATHS.EDIT_OKR(
                    projectId,
                    okrId,
                  ),
                )
              }
            >
              <PencilIcon className="w-4 h-4" />
              {t("projects:edit")}
            </Button>
          </>
        ) : undefined
      }
    >
      {okr ? (
        <>
          <h5 className="mt-3 mb-8 font-medium">
            {t("projects:objective_information")}
          </h5>
          <div className="flex flex-col w-full mb-8 gap-7">
            <DocumentReviewDetailItem
              icon={<FileText className="w-5 h-5 text-primary" />}
              color="primary/10"
              label={t("projects:objective")}
              value={okr.name}
              valueClassName="font-medium"
            />
            {okr.description && (
              <DocumentReviewDetailItem
                icon={<FileText className="w-5 h-5 text-primary" />}
                color="primary/10"
                label={t("projects:description")}
                value={okr.description}
                valueClassName="font-medium"
              />
            )}
            <DocumentReviewDetailItem
              icon={<Activity className="w-5 h-5 text-primary" />}
              color="primary/10"
              label={t("projects:status")}
              value={t(`projects:${okr.status}`)}
              valueClassName="font-medium uppercase"
            />
            <DocumentReviewDetailItem
              icon={<Calendar className="w-5 h-5 text-[#1AD598]" />}
              color="[#1AD5981A]"
              label={t("projects:created_on")}
              value={formatDate(new Date(okr.createdAt), "dd MMM yyyy")}
              valueClassName="font-medium"
            />
          </div>

          {okr.projectKeyResults?.length > 0 && (
            <div className="mt-2 pt-6 border-t border-border-gray-primary">
              <h5 className="font-medium mb-4">
                {t("projects:key_results_count", {
                  count: okr.projectKeyResults.length,
                })}
              </h5>
              <div className="flex flex-col gap-3">
                {okr.projectKeyResults.map((kr) => (
                  <div key={kr.id} className="p-3 rounded-md bg-muted/40">
                    <span className="text-sm font-medium">{kr.name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      ) : (
        !isLoading && (
          <p className="text-sm text-muted-foreground mt-6">
            {t("projects:okr_not_found")}
          </p>
        )
      )}
    </DocumentDetailLayout>
  );
};
